import React, { useState, useEffect, useRef } from 'react';
import './HotspotStep.css';

const HotspotStep = ({ mediaUrl, contentHtml, minScoreToPass, onComplete }) => {
    let hotspots = [];
    try {
        hotspots = contentHtml ? JSON.parse(contentHtml) : [];
    } catch (e) {
        console.error('Error parsing hotspots JSON:', e);
    }

    const targetHotspots = hotspots.filter(h => h.isCorrect);
    const totalTargets = targetHotspots.length;

    const [foundIndices, setFoundIndices] = useState(new Set());
    const [selectedHotspot, setSelectedHotspot] = useState(null);
    const [showIncorrectEffect, setShowIncorrectEffect] = useState(null);
    const [imageStatus, setImageStatus] = useState('loading');
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!mediaUrl) return;
        setImageStatus('loading');
        const img = new Image();
        img.onload = () => setImageStatus('loaded');
        img.onerror = () => setImageStatus('error');
        img.src = mediaUrl;
    }, [mediaUrl]);

    const handleImageClick = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const clickX = ((e.clientX - rect.left) / rect.width) * 100;
        const clickY = ((e.clientY - rect.top) / rect.height) * 100;

        let hitIdx = -1;
        for (let i = 0; i < hotspots.length; i++) {
            const h = hotspots[i];
            const inX = clickX >= h.x && clickX <= (h.x + h.width);
            const inY = clickY >= h.y && clickY <= (h.y + h.height);
            if (inX && inY) {
                hitIdx = i;
                break;
            }
        }

        if (hitIdx !== -1) {
            const hit = hotspots[hitIdx];
            setSelectedHotspot(hit);
            if (hit.isCorrect) {
                setFoundIndices(prev => {
                    const next = new Set(prev);
                    next.add(hitIdx);
                    return next;
                });
            } else {
                setShowIncorrectEffect({ x: clickX, y: clickY });
                setTimeout(() => setShowIncorrectEffect(null), 800);
            }
        } else {
            setSelectedHotspot(null);
            setShowIncorrectEffect({ x: clickX, y: clickY });
            setTimeout(() => setShowIncorrectEffect(null), 800);
        }
    };

    const foundCount = hotspots.filter((h, idx) => h.isCorrect && foundIndices.has(idx)).length;
    const score = totalTargets > 0 ? Math.round((foundCount / totalTargets) * 100) : 100;
    const isPassed = score >= (minScoreToPass || 80);

    useEffect(() => {
        if (isPassed && onComplete) {
            onComplete(score);
        }
    }, [score, isPassed, onComplete]);

    if (!mediaUrl) {
        return (
            <div className="hotspots-empty">
                <p>⚠️ Зображення для активних зон не завантажене.</p>
            </div>
        );
    }

    return (
        <div className="hotspots-step-container">
            <div className="hotspots-header-brief">
                <div className="task-banner">
                    🎯 <strong>Завдання:</strong> Знайдіть усі приховані помилки чи цільові елементи на зображенні нижче.
                </div>
                <div className="hotspots-score-badge">
                    Знайдено: <strong>{foundCount} з {totalTargets}</strong> ({score}%)
                    {isPassed ? <span className="badge-pass">✅ Пройдено</span> : <span className="badge-fail">❌ Недостатньо ({minScoreToPass || 80}% для проходження)</span>}
                </div>
            </div>

            <div className="hotspots-canvas-wrapper">
                <div
                    ref={canvasRef}
                    className="hotspots-canvas"
                    onClick={handleImageClick}
                    style={{ backgroundImage: `url(${mediaUrl})` }}
                >
                    {imageStatus === 'loading' && (
                        <div className="hotspots-image-loading">
                            <div className="loading-spinner" />
                            <p>Завантаження зображення...</p>
                        </div>
                    )}
                    {imageStatus === 'error' && (
                        <div className="hotspots-image-error">
                            <p>❌ Не вдалося завантажити зображення</p>
                            <p className="hotspots-image-error-url">{mediaUrl}</p>
                        </div>
                    )}
                    {hotspots.map((h, idx) => {
                        const isFound = foundIndices.has(idx);
                        if (!isFound) return null;
                        return (
                            <div
                                key={idx}
                                className="hotspot-overlay-found"
                                style={{
                                    left: `${h.x}%`,
                                    top: `${h.y}%`,
                                    width: `${h.width}%`,
                                    height: `${h.height}%`
                                }}
                                title={h.title}
                            />
                        );
                    })}
                    {showIncorrectEffect && (
                        <div
                            className="incorrect-click-ripple"
                            style={{
                                left: `${showIncorrectEffect.x}%`,
                                top: `${showIncorrectEffect.y}%`
                            }}
                        />
                    )}
                </div>
            </div>

            {selectedHotspot && (
                <div className={`hotspot-detail-card ${selectedHotspot.isCorrect ? 'correct' : 'incorrect'}`}>
                    <span className="detail-status-icon">
                        {selectedHotspot.isCorrect ? '✅ Знайдено помилку!' : 'ℹ️ Інформаційна зона'}
                    </span>
                    <h4>{selectedHotspot.title || 'Помилка'}</h4>
                </div>
            )}
        </div>
    );
};

export default HotspotStep;
